import { useIntl } from 'react-intl';
import { Col, Input, Row, Select } from 'antd';

import { ExtraFiltersProps } from '@/types/ExtraFiltersProps';

import OrderExtraFilter from '@/components/_common/ExtraFilters/OrderExtraFilter';
import {
  BankingEntryQuery,
  Destination,
} from '@/core/domain/financial/BankingEntry/BankingEntry.types';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import {
  CompaniesInfiniteSelectField,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import moment from 'moment';
import { useBankAccounts } from '@/core/domain/enterprise/BankAccounts/BankAccounts.hooks';

function BankingEntryExtraFilters({
  queryParams,
  setQueryParams,
}: ExtraFiltersProps<BankingEntryQuery>) {
  const intl = useIntl();

  const { data: bankAccountData, isFetching: bankAccontsFetching } =
    useBankAccounts({
      rows: 999,
    });

  const bankAccounts = flatInfiniteData(bankAccountData);

  return (
    <Row gutter={[16, 16]}>
      <Col sm={24} lg={8} md={12}>
        <CompaniesInfiniteSelectField
          placeholder={intl.formatMessage({
            id: 'releaseBankEntry.list.header.companies',
          })}
          allowClear
          style={{ width: '100%' }}
          value={queryParams?.companyId}
          onChange={(values) =>
            setQueryParams((prev) => ({ ...prev, companyId: values }))
          }
        />
      </Col>

      <Col sm={24} lg={4} md={12}>
        <Input
          value={queryParams?.code}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, code: value }))
          }
          placeholder={intl.formatMessage({
            id: 'releaseBankEntry.list.header.code',
          })}
        />
      </Col>

      <Col sm={24} lg={8} md={12}>
        <Input
          value={queryParams?.description}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, description: value }))
          }
          placeholder={intl.formatMessage({
            id: 'releaseBankEntry.list.header.description',
          })}
        />
      </Col>

      <Col sm={24} lg={4} md={12}>
        <Input
          value={queryParams?.documentNumber}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, documentNumber: value }))
          }
          placeholder={intl.formatMessage({
            id: 'releaseBankEntry.list.header.document',
          })}
        />
      </Col>

      <Col sm={24} lg={4} md={12}>
        <InputDate
          placeholder={intl.formatMessage({
            id: 'releaseBankEntry.list.header.releaseDate',
          })}
          value={
            queryParams?.creationDate
              ? moment(queryParams?.creationDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              creationDate: value?.toISOString(),
            }))
          }
        />
      </Col>

      <Col sm={24} lg={3} md={12}>
        <InputDecimal
          value={queryParams?.value}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, value: value }))
          }
          placeholder={intl.formatMessage({
            id: 'releaseBankEntry.list.header.value',
          })}
        />
      </Col>

      <Col sm={24} lg={9} md={12}>
        <Select
          placeholder={intl.formatMessage({
            id: 'releaseBankEntry.list.header.account',
          })}
          allowClear
          loading={bankAccontsFetching}
          style={{ width: '100%' }}
          onChange={(values) =>
            setQueryParams((prev) => ({ ...prev, bankAccountId: values }))
          }
          value={queryParams?.bankAccountId}
          options={bankAccounts?.map((bank) => ({
            value: bank.id,
            label: `${bank.number} - ${bank.bank.name}`,
          }))}
        />
      </Col>

      <Col sm={24} lg={4} md={12}>
        <Select
          allowClear
          style={{ width: '100%' }}
          value={queryParams?.nature}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, nature: value }))
          }
          placeholder={intl.formatMessage({
            id: 'releaseBankEntry.list.header.destination',
          })}
          options={[
            {
              value: Destination.expense,
              label: 'Despesa',
            },
            {
              value: Destination.revenue,
              label: 'Receita',
            },
          ]}
        />
      </Col>

      <Col sm={24} lg={4} md={12}>
        <OrderExtraFilter
          value={queryParams?.order}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              order: value,
            }))
          }
        />
      </Col>
    </Row>
  );
}

export default BankingEntryExtraFilters;
